import { Form, Input, Button, Card, Alert, Divider } from 'antd';
import { UserOutlined, LockOutlined, WindowsOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useEffect } from 'react';

export function Login() {
  const navigate = useNavigate();
  const { login, confirmNewPassword, loading, error, clearError, isAuthenticated, challengeName, loginWithMicrosoft } = useAuthStore();

  useEffect(() => {
    // If already authenticated, redirect to home
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Clear error when component unmounts
    return () => clearError();
  }, [clearError]);

  const onFinish = async (values: { email: string; password: string }) => {
    try {
      await login(values.email, values.password);
      // Don't navigate here - wait for challenge or success
    } catch (err) {
      // Error is already handled in the store
      console.error('Login error:', err);
    }
  };

  const onNewPasswordFinish = async (values: { name: string; newPassword: string; confirmPassword: string }) => {
    try {
      await confirmNewPassword(values.newPassword, values.name);
      navigate('/');
    } catch (err) {
      console.error('New password error:', err);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      await loginWithMicrosoft();
      // User will be redirected - no need to handle response here
    } catch (err) {
      console.error('Microsoft login failed:', err);
    }
  };

  // Show new password form if challenge is required
  if (challengeName === 'NEW_PASSWORD_REQUIRED') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5'
      }}>
        <Card
          title="Set New Password"
          style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Alert
            message="Password Change Required"
            description="Please set a new password to continue."
            type="info"
            style={{ marginBottom: 24 }}
          />

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              closable
              onClose={clearError}
              style={{ marginBottom: 24 }}
            />
          )}

          <Form
            name="newPassword"
            onFinish={onNewPasswordFinish}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="name"
              rules={[
                { required: true, message: 'Please input your name!' },
                { min: 2, message: 'Name must be at least 2 characters!' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Full Name"
                autoComplete="name"
              />
            </Form.Item>

            <Form.Item
              name="newPassword"
              rules={[
                { required: true, message: 'Please input your new password!' },
                { min: 8, message: 'Password must be at least 8 characters!' },
                { pattern: /[A-Z]/, message: 'Password must contain uppercase letter!' },
                { pattern: /[a-z]/, message: 'Password must contain lowercase letter!' },
                { pattern: /[0-9]/, message: 'Password must contain a number!' },
                { pattern: /[^A-Za-z0-9]/, message: 'Password must contain a special character!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="New Password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm Password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                Set New Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  // Show login form
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f0f2f5'
    }}>
      <Card
        title="Mapper UI Login"
        style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        {error && (
          <Alert
            message="Login Failed"
            description={error}
            type="error"
            closable
            onClose={clearError}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Email"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              Log in
            </Button>
          </Form.Item>
        </Form>

        <Divider plain>or</Divider>

        <Button
          block
          size="large"
          icon={<WindowsOutlined />}
          onClick={handleMicrosoftLogin}
          loading={loading}
          style={{
            backgroundColor: '#0078d4',
            color: 'white',
            borderColor: '#0078d4',
          }}
        >
          Sign in with Microsoft
        </Button>
      </Card>
    </div>
  );
}
